import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { DashboardLayout } from '../../Components/Layout/DashboardLayout';
import { toast } from 'react-toastify';
import {
  useGetPathsQuery,
  useGetLandmarksQuery,
  useCreatePathMutation,
  useUpdatePathMutation,
  useDeletePathMutation,
  useRebuildGraphMutation,
  useGetGraphStatsQuery
} from '../../Redux/Apis/Navigation';
import CustomButton from '../../Components/CustomButton';
import './style.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PathEditor = () => {
  const { data: pathsData, refetch: refetchPaths } = useGetPathsQuery({});
  const { data: landmarksData } = useGetLandmarksQuery({});
  const { data: graphStats } = useGetGraphStatsQuery();
  const [createPath] = useCreatePathMutation();
  const [updatePath] = useUpdatePathMutation();
  const [deletePath] = useDeletePathMutation();
  const [rebuildGraph, { isLoading: isRebuilding }] = useRebuildGraphMutation();

  const [drawingMode, setDrawingMode] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [showPathForm, setShowPathForm] = useState(false);

  // Form state
  const [pathForm, setPathForm] = useState({
    name: '',
    description: '',
    pathType: 'cart_path',
    allowedTransports: ['golf_cart', 'walking'],
    speedLimit: 15,
    isOneWay: false,
    status: 'active'
  });

  const paths = pathsData?.data?.paths || [];
  const landmarks = landmarksData?.data?.landmarks || [];

  const defaultCenter = [31.9513, -81.0585]; // Default golf course location

  const handleMapClick = (e) => {
    if (drawingMode) {
      const newPoint = [e.latlng.lat, e.latlng.lng];
      setCurrentPath([...currentPath, newPoint]);
    }
  };

  const MapClickHandler = () => {
    useMapEvents({
      click: handleMapClick
    });
    return null;
  };

  const startDrawing = () => {
    setDrawingMode(true);
    setCurrentPath([]);
    setSelectedPath(null);
    toast.info('Click on the map to draw a path. Double-click to finish.');
  };

  const finishDrawing = () => {
    if (currentPath.length < 2) {
      toast.error('A path must have at least 2 points');
      return;
    }
    setDrawingMode(false);
    setShowPathForm(true);
  };

  const cancelDrawing = () => {
    setDrawingMode(false);
    setCurrentPath([]);
    setShowPathForm(false);
  };

  const savePath = async () => {
    if (!pathForm.name) {
      toast.error('Please enter a path name');
      return;
    }

    if (currentPath.length < 2) {
      toast.error('A path must have at least 2 points');
      return;
    }

    // Convert to GeoJSON format [longitude, latitude]
    const coordinates = currentPath.map(point => [point[1], point[0]]);

    const pathData = {
      ...pathForm,
      geometry: {
        type: 'LineString',
        coordinates
      }
    };

    try {
      if (selectedPath) {
        await updatePath({ id: selectedPath._id, ...pathData }).unwrap();
        toast.success('Path updated successfully');
      } else {
        await createPath(pathData).unwrap();
        toast.success('Path created successfully');
      }
      
      refetchPaths();
      cancelDrawing();
      resetForm();
    } catch (err) {
      toast.error('Failed to save path');
    }
  };

  const resetForm = () => {
    setPathForm({
      name: '',
      description: '',
      pathType: 'cart_path',
      allowedTransports: ['golf_cart', 'walking'],
      speedLimit: 15,
      isOneWay: false,
      status: 'active'
    });
  };

  const editPath = (path) => {
    setSelectedPath(path);
    // Convert GeoJSON coordinates back to [lat, lng]
    const coordinates = path.geometry.coordinates.map(coord => [coord[1], coord[0]]);
    setCurrentPath(coordinates);
    setPathForm({
      name: path.name,
      description: path.description || '',
      pathType: path.pathType,
      allowedTransports: path.allowedTransports,
      speedLimit: path.speedLimit,
      isOneWay: path.isOneWay,
      status: path.status
    });
    setShowPathForm(true);
    setDrawingMode(true);
  };

  const handleDeletePath = async (pathId) => {
    if (window.confirm('Are you sure you want to delete this path?')) {
      try {
        await deletePath(pathId).unwrap();
        toast.success('Path deleted successfully');
        refetchPaths();
      } catch (err) {
        toast.error('Failed to delete path');
      }
    }
  };

  const handleRebuildGraph = async () => {
    if (window.confirm('Rebuild the routing graph? This may take a few moments.')) {
      try {
        const result = await rebuildGraph().unwrap();
        toast.success(`Graph rebuilt: ${result.data.nodeCount} nodes, ${result.data.edgeCount} edges`);
      } catch (err) {
        toast.error('Failed to rebuild graph');
      }
    }
  };

  const handleTransportToggle = (transport) => {
    const transports = [...pathForm.allowedTransports];
    const index = transports.indexOf(transport);
    
    if (index > -1) {
      if (transports.length > 1) {
        transports.splice(index, 1);
      } else {
        toast.warning('At least one transport type must be selected');
        return;
      }
    } else {
      transports.push(transport);
    }
    
    setPathForm({ ...pathForm, allowedTransports: transports });
  };

  const getPathColor = (pathType) => {
    switch (pathType) {
      case 'cart_path':
        return '#4CAF50'; // Green
      case 'street':
        return '#2196F3'; // Blue
      case 'mixed':
        return '#FF9800'; // Orange
      default:
        return '#757575'; // Gray
    }
  };

  return (
    <DashboardLayout>
      <div className="pathEditorContainer">
        <div className="pathEditorHeader">
          <h2>Cart Path & Route Editor</h2>
          
          <div className="statsPanel">
            <div className="stat">
              <span className="statLabel">Paths:</span>
              <span className="statValue">{graphStats?.data?.stats?.paths || 0}</span>
            </div>
            <div className="stat">
              <span className="statLabel">Nodes:</span>
              <span className="statValue">{graphStats?.data?.stats?.nodes || 0}</span>
            </div>
            <div className="stat">
              <span className="statLabel">Edges:</span>
              <span className="statValue">{graphStats?.data?.stats?.edges || 0}</span>
            </div>
          </div>

          <div className="headerActions">
            {!drawingMode && !showPathForm && (
              <>
                <CustomButton
                  text="Draw New Path"
                  onClick={startDrawing}
                  variant="primaryButton"
                />
                <CustomButton
                  text="Rebuild Graph"
                  onClick={handleRebuildGraph}
                  variant="secondaryButton"
                  disabled={isRebuilding}
                />
              </>
            )}
            
            {drawingMode && (
              <>
                <CustomButton
                  text="Finish Drawing"
                  onClick={finishDrawing}
                  variant="successButton"
                  disabled={currentPath.length < 2}
                />
                <CustomButton
                  text="Cancel"
                  onClick={cancelDrawing}
                  variant="dangerButton"
                />
              </>
            )}
          </div>
        </div>

        <div className="pathEditorContent">
          <div className="mapPanel">
            <MapContainer
              center={defaultCenter}
              zoom={15}
              style={{ height: '600px', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              <MapClickHandler />

              {/* Existing paths */}
              {paths.map((path) => {
                // Convert GeoJSON to Leaflet format
                const positions = path.geometry.coordinates.map(coord => [coord[1], coord[0]]);
                return (
                  <Polyline
                    key={path._id}
                    positions={positions}
                    color={getPathColor(path.pathType)}
                    weight={5}
                    opacity={0.7}
                    eventHandlers={{
                      click: () => {
                        if (!drawingMode) {
                          setSelectedPath(path);
                        }
                      }
                    }}
                  >
                    <Popup>
                      <div>
                        <h4>{path.name}</h4>
                        <p>{path.pathType}</p>
                        <p>Speed: {path.speedLimit} mph</p>
                        <div className="popupActions">
                          <button onClick={() => editPath(path)}>Edit</button>
                          <button onClick={() => handleDeletePath(path._id)}>Delete</button>
                        </div>
                      </div>
                    </Popup>
                  </Polyline>
                );
              })}

              {/* Current path being drawn */}
              {currentPath.length > 0 && (
                <Polyline
                  positions={currentPath}
                  color="#FF5722"
                  weight={5}
                  dashArray="10, 10"
                />
              )}

              {/* Landmarks */}
              {landmarks.map((landmark) => {
                const position = [landmark.location.coordinates[1], landmark.location.coordinates[0]];
                return (
                  <Marker key={landmark._id} position={position}>
                    <Popup>
                      <div>
                        <h4>{landmark.name}</h4>
                        <p>{landmark.category}</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>

            {drawingMode && (
              <div className="drawingInstructions">
                <p>🖱️ Click on the map to add points to your path</p>
                <p>✓ Click "Finish Drawing" when done (minimum 2 points)</p>
              </div>
            )}
          </div>

          {showPathForm && (
            <div className="pathFormPanel">
              <h3>{selectedPath ? 'Edit Path' : 'New Path'}</h3>
              
              <div className="formGroup">
                <label>Path Name *</label>
                <input
                  type="text"
                  value={pathForm.name}
                  onChange={(e) => setPathForm({ ...pathForm, name: e.target.value })}
                  placeholder="E.g., Main Cart Path"
                />
              </div>

              <div className="formGroup">
                <label>Description</label>
                <textarea
                  value={pathForm.description}
                  onChange={(e) => setPathForm({ ...pathForm, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>

              <div className="formGroup">
                <label>Path Type</label>
                <select
                  value={pathForm.pathType}
                  onChange={(e) => setPathForm({ ...pathForm, pathType: e.target.value })}
                >
                  <option value="cart_path">Golf Cart Path</option>
                  <option value="street">Street</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>

              <div className="formGroup">
                <label>Allowed Transport</label>
                <div className="checkboxGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={pathForm.allowedTransports.includes('golf_cart')}
                      onChange={() => handleTransportToggle('golf_cart')}
                    />
                    Golf Cart
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={pathForm.allowedTransports.includes('walking')}
                      onChange={() => handleTransportToggle('walking')}
                    />
                    Walking
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={pathForm.allowedTransports.includes('car')}
                      onChange={() => handleTransportToggle('car')}
                    />
                    Car
                  </label>
                </div>
              </div>

              <div className="formGroup">
                <label>Speed Limit (mph)</label>
                <input
                  type="number"
                  value={pathForm.speedLimit}
                  onChange={(e) => setPathForm({ ...pathForm, speedLimit: parseInt(e.target.value) })}
                  min="1"
                  max="50"
                />
              </div>

              <div className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    checked={pathForm.isOneWay}
                    onChange={(e) => setPathForm({ ...pathForm, isOneWay: e.target.checked })}
                  />
                  One-way path
                </label>
              </div>

              <div className="formActions">
                <CustomButton
                  text="Save Path"
                  onClick={savePath}
                  variant="successButton"
                />
                <CustomButton
                  text="Cancel"
                  onClick={cancelDrawing}
                  variant="secondaryButton"
                />
              </div>
            </div>
          )}

          {selectedPath && !showPathForm && (
            <div className="pathDetailsPanel">
              <h3>{selectedPath.name}</h3>
              <p><strong>Type:</strong> {selectedPath.pathType}</p>
              <p><strong>Speed:</strong> {selectedPath.speedLimit} mph</p>
              <p><strong>Transports:</strong> {selectedPath.allowedTransports.join(', ')}</p>
              <p><strong>One-way:</strong> {selectedPath.isOneWay ? 'Yes' : 'No'}</p>
              {selectedPath.description && <p>{selectedPath.description}</p>}
              
              <div className="detailActions">
                <CustomButton
                  text="Edit"
                  onClick={() => editPath(selectedPath)}
                  variant="primaryButton"
                />
                <CustomButton
                  text="Delete"
                  onClick={() => handleDeletePath(selectedPath._id)}
                  variant="dangerButton"
                />
                <CustomButton
                  text="Close"
                  onClick={() => setSelectedPath(null)}
                  variant="secondaryButton"
                />
              </div>
            </div>
          )}
        </div>

        <div className="legend">
          <h4>Path Types</h4>
          <div className="legendItem">
            <div className="legendColor" style={{ backgroundColor: '#4CAF50' }}></div>
            <span>Golf Cart Path</span>
          </div>
          <div className="legendItem">
            <div className="legendColor" style={{ backgroundColor: '#2196F3' }}></div>
            <span>Street</span>
          </div>
          <div className="legendItem">
            <div className="legendColor" style={{ backgroundColor: '#FF9800' }}></div>
            <span>Mixed</span>
          </div>
          <div className="legendItem">
            <div className="legendColor" style={{ backgroundColor: '#FF5722', border: '2px dashed #FF5722' }}></div>
            <span>Drawing...</span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PathEditor;
